'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LiveCallStrip } from '@/components/live-call-strip';
import { usePoolStream } from '@/hooks/use-pool-stream';
import { ApiError, fetchJson } from '@/lib/api/client';
import { isPooledAgentLine } from '@/lib/call-utils';
import { pooledDialerApi } from '@/lib/api/pooled-dialer';
import { contactDetailRequest } from '@/lib/api/keys';
import { recordClientTelemetryEvent } from '@/lib/client-telemetry';
import { playBridgeChime } from '@/lib/pool-chime';
import { createLineHealthBreaker, isLinePreconditionFailure, reconcilePoolJoin } from '@/lib/pool-lifecycle';
import { SERVER_RESTART_GIVE_UP_MS, stepServerRestart, type ServerRestart, type ServerRestartPoll } from '@/lib/pool-restart';
import {
  classifyBusyEntry,
  lastObservedContactId,
  lastObservedState,
  manualHandoffStarted,
  publicationLagMs,
  screenPopRetryDecision,
  snapshotAgeMs,
  SCREEN_POP_RETRY_DELAY_MS,
  type PoolScreenPopOutcome,
  type PoolScreenPopSource,
} from '@/lib/pool-screen-pop';
import { snapshotLagging } from '@/lib/pool-stream-health';
import {
  carriedCallEnded,
  claimReady,
  LIVE_CALL_STORAGE_KEY,
  liveCallMarkerStep,
  parseLiveCallMarker,
  rescueWatchStep,
  type PoolLiveCallMarker,
  type RescueWatch,
} from '@/lib/pool-call-rescue';
import { answeredLegsOldestFirst, upNextLegFor } from '@/lib/pool-view';
import { useAuthSession } from '@/providers/app-runtime';
import { useSoftphoneRuntime } from '@/providers/softphone-context';
import type { Contact } from '@/types/contacts';
import {
  POOL_CALL_RESCUE_WINDOW_MS,
  type PooledDialerLegView,
  type PooledDialerMemberState,
  type PooledDialerMemberView,
} from '../../../shared/types/pooled-dialer';

const MEMBERSHIP_POLL_MS = 3000;
const MEMBERSHIP_QUERY_KEY = ['pooled-dialer', 'membership'] as const;
const REJOIN_INITIAL_DELAY_MS = 2_000;
const REJOIN_RETRY_MS = 10_000;
// After a registration-gate refusal the retry follows the phone's own
// registered state on a short cadence instead of the blind timer.
const REJOIN_LINE_RETRY_MS = 3_000;
const POOL_INTENT_CHANNEL = 'pooledDialer.intent';
// Set right before a self-reload for a CRM update; the new page re-arms the
// join from it. Per tab, one-shot.
const REJOIN_AFTER_RELOAD_KEY = 'pooledDialer.rejoinAfterReload';
// In the pool with nothing owed: no conversation, no disposition, no locked
// contact. A reload here costs one rejoin and nothing else.
const PARKED_STATES: ReadonlySet<PooledDialerMemberState> = new Set(['connecting', 'free', 'idle']);
// A claim refused because the call cannot be carried over yet is asked again
// on this cadence, inside the rescue window.
const RESCUE_CLAIM_RETRY_MS = 2_000;

interface PoolIntent {
  id: string;
  campaignId: string | null;
}

/** Everything the gate knew about one busy tenure, held until its pop
 * resolves. The three timestamps are monotonic (performance.now()) on
 * purpose: agent clocks here drift by minutes and get stepped by NTP
 * mid-shift, so no wall-clock difference can be trusted to time anything. */
interface PendingScreenPop {
  busyObservedAt: number;
  /** When the server first named a contact — until then there is nowhere to
   * pop to, and the wait is the server's, not the client's. */
  contactObservedAt: number | null;
  /** The contact route we pushed to, awaiting its commit. Null until pushed. */
  target: string | null;
  /** Route the browser sat on when the push was issued — the retry refuses to
   * re-push once the agent has moved somewhere else themselves. */
  pushedFrom: string | null;
  /** Pushes issued for this tenure, the first included. */
  navAttempts: number;
  contactId: string | null;
  stateChangedAt: string | null;
  publicationLagMs: number | null;
  source: PoolScreenPopSource;
  live: boolean;
  quietRefreshFailures: number;
  snapshotAgeMs: number | null;
  previousState: PooledDialerMemberState | null;
  busyNavDoneAtEntry: boolean;
  legId: string | null;
}

// Exactly one event per busy tenure: the bridge either put the contact card on
// screen or it did not, and which rule stopped it is the whole point — a
// suppressed pop looks like nothing at all from both the floor and the server.
//
// The window is split because the complaint ("live with the prospect, no card
// yet") spans two segments with completely different owners:
//   attributionWaitMs — busy observed until the server names a contact. The
//     engine flips a member busy BEFORE the bridge completes, so this covers
//     the bridge round trip; the agent is not yet talking for most of it.
//   navRenderMs — contact known until the route actually commits. /contacts/[id]
//     is a server component that fetches four endpoints before returning any
//     HTML, and those run in the Next server process rather than the browser,
//     so no other telemetry can see them. This is where the visible stall is.
// popElapsedMs keeps the whole window so neither boundary choice hides time.
function reportScreenPop(outcome: PoolScreenPopOutcome, pending: PendingScreenPop): void {
  const resolvedAt = performance.now();
  recordClientTelemetryEvent(
    'pool_screen_pop',
    outcome === 'no_contact_id' || outcome === 'card_not_reached' ? 'warning' : 'info',
    {
      outcome,
      popElapsedMs: Math.round(resolvedAt - pending.busyObservedAt),
      attributionWaitMs:
        pending.contactObservedAt === null
          ? null
          : Math.round(pending.contactObservedAt - pending.busyObservedAt),
      navRenderMs:
        pending.contactObservedAt === null
          ? null
          : Math.round(resolvedAt - pending.contactObservedAt),
      publicationLagMs: pending.publicationLagMs,
      source: pending.source,
      live: pending.live,
      quietRefreshFailures: pending.quietRefreshFailures,
      snapshotAgeMs: pending.snapshotAgeMs,
      previousState: pending.previousState,
      busyNavDoneAtEntry: pending.busyNavDoneAtEntry,
      navAttempts: pending.navAttempts,
      stateChangedAt: pending.stateChangedAt,
      legId: pending.legId,
      contactId: pending.contactId,
    }
  );
}

function resolvePendingScreenPop(
  ref: RefObject<PendingScreenPop | null>,
  outcome: PoolScreenPopOutcome
): void {
  const pending = ref.current;
  if (!pending) return;
  ref.current = null;
  reportScreenPop(outcome, pending);
}

/** Delivery conditions sampled during render, so the effect reports what
 * produced an observation rather than whatever is current when it runs. */
interface ScreenPopContext {
  source: PoolScreenPopSource;
  live: boolean;
  quietRefreshFailures: number;
  updatedAt: number;
  serverAt: string | null;
}

/** The gate's cross-render state, bundled so the step below stays one call. */
interface ScreenPopRefs {
  pending: RefObject<PendingScreenPop | null>;
  navDone: RefObject<boolean>;
  pathname: RefObject<string>;
  context: RefObject<ScreenPopContext>;
}

function openScreenPop(
  context: ScreenPopContext,
  previous: PooledDialerMemberState | null,
  member: PooledDialerMemberView | null,
  navDone: boolean
): PendingScreenPop {
  return {
    busyObservedAt: performance.now(),
    contactObservedAt: null,
    target: null,
    pushedFrom: null,
    navAttempts: 0,
    contactId: member?.currentContactId ?? null,
    stateChangedAt: member?.stateChangedAt ?? null,
    publicationLagMs: publicationLagMs(member?.stateChangedAt ?? null, context.serverAt),
    source: context.source,
    live: context.live,
    quietRefreshFailures: context.quietRefreshFailures,
    snapshotAgeMs: snapshotAgeMs(Date.now(), context.updatedAt),
    previousState: previous,
    busyNavDoneAtEntry: navDone,
    legId: member?.currentLegId ?? null,
  };
}

/**
 * Advances one `busy` observation: opens a tenure on a fresh bridge, chimes,
 * and hands over the contact card once the server names one. Returns the route
 * to push, or null when nothing should move.
 */
function stepScreenPop(
  refs: ScreenPopRefs,
  previous: PooledDialerMemberState | null,
  member: PooledDialerMemberView | null
): string | null {
  const entry = classifyBusyEntry(previous);
  if (entry !== 'ongoing') {
    refs.pending.current = openScreenPop(refs.context.current, previous, member, refs.navDone.current);
  }
  if (entry === 'mid_call_reload') {
    refs.navDone.current = true; // reloaded mid-call: stay put
    resolvePendingScreenPop(refs.pending, 'mid_call_reload');
    return null;
  }
  if (entry === 'bridged') {
    playBridgeChime();
    refs.navDone.current = false;
  }
  const contactId = member?.currentContactId;
  if (refs.navDone.current || !contactId) return null;
  refs.navDone.current = true;
  const target = `/contacts/${contactId}`;
  const pending = refs.pending.current;
  if (pending) {
    pending.contactObservedAt = performance.now();
    pending.contactId = contactId;
    pending.target = target;
  }
  if (refs.pathname.current === target) {
    resolvePendingScreenPop(refs.pending, 'already_there');
    return null;
  }
  if (pending) {
    pending.pushedFrom = refs.pathname.current;
    pending.navAttempts = 1;
  }
  // Resolved by the pathname effect once the route actually commits — push
  // only starts the server-component fetches.
  return target;
}

interface PoolMembershipValue {
  membership: {
    campaignId: string;
    member: PooledDialerMemberView;
  } | null;
  // A screened-out candidate may change this best-effort preview.
  upNext: PooledDialerLegView | null;
  refresh: () => void;
  join: (campaignId: string) => Promise<void>;
  leave: () => Promise<void>;
  joining: boolean;
  leaving: boolean;
  desiredCampaignId: string | null;
  lineWarning: boolean;
  busyWithNonPoolCall: boolean;
  orphanedPoolLine: boolean;
  phoneReconnecting: boolean;
  /**
   * The backend is restarting for a deploy (or has just gone quiet while we
   * were seated): the seat is coming back on its own, so surfaces should say
   * so instead of showing the lost-line state.
   */
  serverRestarting: boolean;
  /** In the pool with no conversation or disposition owed. */
  parked: boolean;
  /**
   * Leave a parked line so the page can reload itself, arming a rejoin in
   * the new page. Resolves false when a call connected in the meantime — the
   * reload must wait. True when there was nothing to release.
   */
  releaseLineForReload: () => Promise<boolean>;
}

const PoolMembershipContext = createContext<PoolMembershipValue | null>(null);

export function usePoolMembership(): PoolMembershipValue {
  const context = useContext(PoolMembershipContext);
  if (!context) {
    throw new Error('usePoolMembership must be used inside PoolMembershipProvider');
  }
  return context;
}

export function PoolMembershipProvider({ children }: { children: ReactNode }) {
  const auth = useAuthSession();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const softphone = useSoftphoneRuntime();
  const user = auth.status === 'authenticated' ? auth.user : null;
  const eligible = Boolean(user && user.role !== 'admin' && !user.impersonatorUsername);

  const busyWithNonPoolCall = Boolean(
    softphone?.activeCall
    && softphone.activeCall.status !== 'ended'
    && softphone.activeCall.source !== 'pooled_dialer'
  );
  const busyWithNonPoolCallRef = useRef(busyWithNonPoolCall);
  busyWithNonPoolCallRef.current = busyWithNonPoolCall;
  // A join originates a SIP call to this browser's phone, so asking while it
  // is mid-reconnect (or errored) can only ring nobody for 20s and surface a
  // server error. Hold joins until the phone is back — in every tab: mirrors
  // receive the owner's reconnecting flag and presented phase over the
  // softphone bus, and a mirror that joined anyway would burn the doomed
  // originate the owner tab is deliberately holding.
  const phoneReconnecting = Boolean(
    softphone
    && (softphone.reconnecting || softphone.state.phase === 'connecting' || softphone.state.phase === 'error')
  );
  // The phone's own view of its registration — what a registration-gated
  // rejoin waits on. Mirrors read the owner's presented phase.
  const phoneRegistered = softphone?.state.phase === 'online';
  const phoneRegisteredRef = useRef(phoneRegistered);
  phoneRegisteredRef.current = phoneRegistered;

  const [desiredCampaignId, setDesiredCampaignId] = useState<string | null>(null);
  const intentRef = useRef<PoolIntent | undefined>(undefined);
  const intentChannelRef = useRef<BroadcastChannel | null>(null);
  const [pendingAction, setPendingAction] = useState<'join' | 'leave' | null>(null);
  const [lineWarning, setLineWarning] = useState(false);
  const breaker = useRef(createLineHealthBreaker()).current;
  const setIntent = useCallback((intent: PoolIntent) => {
    intentRef.current = intent;
    setDesiredCampaignId(intent.campaignId);
  }, []);

  const publishIntent = useCallback((target: string | null) => {
    const intent = { id: crypto.randomUUID(), campaignId: target };
    setIntent(intent);
    try {
      intentChannelRef.current?.postMessage(intent);
    } catch {
      // Cross-tab coordination is best-effort; local intent remains authoritative.
    }
    return intent;
  }, [setIntent]);
  const reconcileJoin = useCallback((target: string) => reconcilePoolJoin({
    target,
    join: (campaignId) => pooledDialerApi.join(campaignId).then(() => undefined),
    leave: (campaignId) => pooledDialerApi.leave(campaignId).then(() => undefined),
    desired: () => intentRef.current?.campaignId ?? null,
  }), []);

  const membershipQuery = useQuery({
    queryKey: MEMBERSHIP_QUERY_KEY,
    queryFn: pooledDialerApi.myMembership,
    enabled: eligible,
    refetchInterval: MEMBERSHIP_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });
  const polled = membershipQuery.data?.membership ?? null;
  const polledServerAt = membershipQuery.data?.serverAt ?? null;

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: MEMBERSHIP_QUERY_KEY });
  }, [queryClient]);

  const streamCampaignId = eligible ? (desiredCampaignId ?? polled?.campaignId ?? null) : null;
  const { pool, live, updatedAt, quietRefreshFailures } = usePoolStream(streamCampaignId);
  const streamMember = live && pool ? (pool.members.find((m) => m.userId === user?.id) ?? null) : null;
  // A live badge is not proof of delivery: a half-open stream keeps pinging
  // while its snapshot freezes. Deferring to a stream in that state discards
  // a 3s poll that already holds the right answer, so the frozen view becomes
  // the only view. snapshotLagging is the same test the floor's "as of"
  // watermark uses — it needs a PROVEN pull failure, so a healthy quiet lull
  // never hands presence over to the poll.
  const streamFrozen = snapshotLagging(Date.now(), live, updatedAt, quietRefreshFailures);
  // The live stream is authoritative for presence and state; the poll covers
  // stream-down windows.
  const streamAuthoritative = Boolean(
    live
    && !streamFrozen
    && (streamMember !== null || !polled || polled.campaignId === streamCampaignId)
  );
  const member = streamAuthoritative ? streamMember : (polled?.member ?? null);
  const campaignId = streamAuthoritative
    ? (streamMember ? streamCampaignId : null)
    : (polled?.campaignId ?? null);
  const membership = useMemo(
    () => (member && campaignId ? { campaignId, member } : null),
    [member, campaignId]
  );
  const parked = membership !== null && PARKED_STATES.has(membership.member.state);
  const orphanedPoolLine = Boolean(
    !membership
    && softphone?.recentCalls.some(
      (call) => call.status !== 'ended' && isPooledAgentLine(call)
    )
  );

  const upNext = useMemo(
    () =>
      live && pool && member
        ? upNextLegFor(pool.members, answeredLegsOldestFirst(pool.legs), member)
        : null,
    [live, pool, member]
  );

  // Warm the likely-next or newly bridged contact card.
  const prefetchContactId =
    (member?.state === 'busy' ? member.currentContactId : null) ?? upNext?.contactId ?? null;
  useEffect(() => {
    if (!prefetchContactId) return;
    const request = contactDetailRequest(prefetchContactId);
    void queryClient.prefetchQuery({
      queryKey: request.key,
      queryFn: ({ signal }) => fetchJson<{ ok: true; contact: Contact }>(request.path, { signal }),
      staleTime: 15_000,
    });
  }, [prefetchContactId, queryClient]);

  // The last state this session actually SAW. `member` alternates between the
  // stream snapshot and the poll, so it blanks on any flap; folding those
  // nulls in here would make a two-second delivery gap indistinguishable from
  // a fresh page load and latch the pop off for the rest of the call.
  const observedStateRef = useRef<PooledDialerMemberState | null>(null);
  const redirectedManualContactRef = useRef<string | null>(null);
  // Busy state can precede contact attribution, so navigation latches for the tenure.
  const busyNavDoneRef = useRef(true);
  const pendingScreenPopRef = useRef<PendingScreenPop | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const screenPopContextRef = useRef<ScreenPopContext>({
    source: 'poll',
    live: false,
    quietRefreshFailures: 0,
    updatedAt: 0,
    serverAt: null,
  });
  screenPopContextRef.current = {
    source: streamAuthoritative ? 'stream' : 'poll',
    live,
    quietRefreshFailures,
    updatedAt,
    // The server clock on whichever payload actually supplied `member`.
    serverAt: streamAuthoritative ? (pool?.serverAt ?? null) : polledServerAt,
  };
  const screenPopRefs = useRef<ScreenPopRefs>({
    pending: pendingScreenPopRef,
    navDone: busyNavDoneRef,
    pathname: pathnameRef,
    context: screenPopContextRef,
  }).current;
  // Folded alongside observedStateRef below, never read raw: the live value is
  // null during a delivery gap, which would read as the server withdrawing the
  // contact and kill the retry mid-call.
  const observedContactIdRef = useRef<string | null>(null);

  // A push whose RSC fetch is dropped mid-flight never commits and reports
  // nothing, so the only way to notice a lost pop is to look again while the
  // call is still live. Each attempt re-arms the next; screenPopRetryDecision
  // owns every stop condition, including the attempt cap.
  const popRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPopRetry = useCallback(() => {
    if (popRetryTimerRef.current === null) return;
    clearTimeout(popRetryTimerRef.current);
    popRetryTimerRef.current = null;
  }, []);
  const armPopRetry = useCallback(function arm(): void {
    if (popRetryTimerRef.current !== null) clearTimeout(popRetryTimerRef.current);
    popRetryTimerRef.current = setTimeout(() => {
      popRetryTimerRef.current = null;
      const pending = pendingScreenPopRef.current;
      if (!pending?.target || !pending.contactId || pending.pushedFrom === null) return;
      const decision = screenPopRetryDecision({
        state: observedStateRef.current,
        contactId: observedContactIdRef.current,
        targetContactId: pending.contactId,
        target: pending.target,
        pathname: pathnameRef.current,
        pushedFrom: pending.pushedFrom,
        navAttempts: pending.navAttempts,
      });
      if (decision !== 'retry') return;
      pending.navAttempts += 1;
      router.push(pending.target);
      arm();
    }, SCREEN_POP_RETRY_DELAY_MS);
  }, [router]);
  useEffect(() => clearPopRetry, [clearPopRetry]);

  useEffect(() => {
    const state = member?.state ?? null;
    const previous = observedStateRef.current;
    observedStateRef.current = lastObservedState(previous, state);
    observedContactIdRef.current = lastObservedContactId(
      observedContactIdRef.current,
      state,
      member?.currentContactId ?? null
    );
    // A null observation is a delivery gap inside the tenure, not its end. A
    // real non-busy state ends it: either a card was owed and never arrived,
    // or the server never named a contact to owe one for.
    if (state !== null && state !== 'busy') {
      clearPopRetry();
      resolvePendingScreenPop(
        pendingScreenPopRef,
        pendingScreenPopRef.current?.target ? 'card_not_reached' : 'no_contact_id'
      );
    }
    if (state === 'busy') {
      const target = stepScreenPop(screenPopRefs, previous, member ?? null);
      if (target) {
        router.push(target);
        armPopRetry();
      }
    } else if (previous === 'wrapup' && (state === 'free' || state === 'connecting') && campaignId) {
      router.push(`/campaigns/${campaignId}/dialer?mode=pooled`);
    } else if (
      manualHandoffStarted(
        previous,
        state,
        member?.currentContactId ?? null,
        redirectedManualContactRef.current
      )
      && member?.manualCampaignId
      && member.currentContactId
      && campaignId
    ) {
      redirectedManualContactRef.current = member.currentContactId;
      // The seat is gone and the page is about to change under the agent. Say
      // why: an unannounced jump out of the pool reads as the pool dropping
      // them, and they re-join into the same handoff until the work is done.
      toast.info(
        member.currentContactName
          ? `${member.currentContactName} is due for a follow-up — complete it and you return to the pool automatically.`
          : 'A follow-up of yours is due — complete it and you return to the pool automatically.'
      );
      router.push(`/campaigns/${member.manualCampaignId}/dialer?mode=manual&returnPoolCampaignId=${campaignId}`);
    }
    if (state !== 'manual') redirectedManualContactRef.current = null;
  }, [
    member?.state,
    member?.currentContactId,
    member?.currentContactName,
    member?.currentLegId,
    member?.stateChangedAt,
    member?.manualCampaignId,
    campaignId,
    router,
    armPopRetry,
    clearPopRetry,
  ]);

  // Closes the pop window where the agent actually sees the card. App Router
  // updates the pathname only once the server component has returned, so this
  // is the first client-observable moment that includes the contact route's
  // own backend fetches — router.push returning means nothing was rendered
  // yet. Any other destination means the agent left before the card landed.
  useEffect(() => {
    const target = pendingScreenPopRef.current?.target;
    if (!target) return;
    clearPopRetry();
    resolvePendingScreenPop(
      pendingScreenPopRef,
      pathname === target ? 'navigated' : 'card_not_reached'
    );
  }, [pathname, clearPopRetry]);

  const join = useCallback(async (target: string) => {
    if (busyWithNonPoolCall) {
      toast.error('Finish the current call before joining the pool.');
      return;
    }
    if (orphanedPoolLine) {
      toast.error('Hang up the remaining pool line in the softphone before joining again.');
      return;
    }
    if (phoneReconnecting) {
      // Arm the intent instead of firing a doomed request: the rejoin effect
      // joins the moment the phone is back.
      publishIntent(target);
      toast.info('Your phone is reconnecting — you will join the pool automatically once it is back.');
      return;
    }
    if (intentRef.current?.campaignId && intentRef.current.campaignId !== target) breaker.reset();
    const intent = publishIntent(target);
    setPendingAction('join');
    try {
      const outcome = await reconcileJoin(target);
      if (outcome.status === 'joined') {
        toast.success('Joined the pool — keep this tab open; calls connect automatically.');
      } else if (outcome.status === 'failed') {
        if (intentRef.current?.id !== intent.id) return;
        publishIntent(null);
        toast.error(outcome.error.message || 'Failed to join the pool');
      } else if (outcome.status === 'cleanup_failed') {
        toast.error(
          `You joined the pool a moment before leaving and the pool could not be exited — press Leave pool again. (${outcome.error.message})`
        );
      }
    } finally {
      setPendingAction((current) => current === 'join' ? null : current);
      refresh();
    }
  }, [breaker, busyWithNonPoolCall, orphanedPoolLine, phoneReconnecting, publishIntent, reconcileJoin, refresh]);

  const leave = useCallback(async () => {
    breaker.reset();
    publishIntent(null);
    if (!campaignId) {
      toast.error('Pool membership is still loading — try again in a moment.');
      return;
    }
    setPendingAction('leave');
    try {
      await pooledDialerApi.leave(campaignId);
      toast.success('You left the pool.');
    } catch (error) {
      toast.error(
        error instanceof Error && error.message ? error.message : 'Could not leave the pool — try again.'
      );
    } finally {
      setPendingAction((current) => current === 'leave' ? null : current);
      refresh();
    }
  }, [breaker, campaignId, publishIntent, refresh]);

  const joinedCampaignId = membership?.campaignId ?? null;
  const joinedCampaignIdRef = useRef(joinedCampaignId);
  joinedCampaignIdRef.current = joinedCampaignId;

  // A backend restart (deploy) releases every seat server-side. Read from the
  // poll — the drain flag on a live answer, or restart-shaped failures while
  // seated — it is a labelled pause, not a phone fault: the intent stays, the
  // breaker is told nothing, and the server's handoff arms a page that has no
  // intent of its own. Declared ahead of the vanish effect below so the
  // breaker is reset before the emptied seat reaches it in the same commit.
  const [serverRestart, setServerRestart] = useState<ServerRestart | null>(null);
  const serverRestartRef = useRef<ServerRestart | null>(null);
  const restartRejoinPendingRef = useRef<{ sinceMs: number } | null>(null);
  const consumedHandoffRef = useRef<string | null>(null);
  const canArmFromHandoffRef = useRef(eligible);
  canArmFromHandoffRef.current = eligible;
  const pollData = membershipQuery.data;
  const pollFailure = membershipQuery.failureReason;
  useEffect(() => {
    const poll: ServerRestartPoll | null = pollFailure
      ? { kind: 'failure', error: pollFailure }
      : pollData
        ? {
            kind: 'response',
            restarting: pollData.restarting,
            membershipCampaignId: pollData.membership?.campaignId ?? null,
            handoffCampaignId: pollData.restartHandoff?.campaignId ?? null,
          }
        : null;
    if (!poll) return;
    const step = stepServerRestart(serverRestartRef.current, {
      nowMs: Date.now(),
      poll,
      memberCampaignId: joinedCampaignIdRef.current,
      intentCampaignId: intentRef.current?.campaignId,
      canArm: canArmFromHandoffRef.current,
    });
    if (step.restart && !serverRestartRef.current) breaker.reset();
    if (step.restart !== serverRestartRef.current) {
      serverRestartRef.current = step.restart;
      setServerRestart(step.restart);
    }
    if (step.armCampaignId) publishIntent(step.armCampaignId);
    if (step.expectRejoin) restartRejoinPendingRef.current = { sinceMs: Date.now() };
    // Once per offer: the Leave reaches the server and the next poll no longer
    // carries it, but the polls in between must not each send another.
    const consumeKey = step.consumeHandoffCampaignId
      ? `${step.consumeHandoffCampaignId}@${pollData?.restartHandoff?.leftAt ?? ''}`
      : null;
    if (step.consumeHandoffCampaignId && consumeKey && consumedHandoffRef.current !== consumeKey) {
      consumedHandoffRef.current = consumeKey;
      pooledDialerApi.leave(step.consumeHandoffCampaignId).then(refresh, () => {
        consumedHandoffRef.current = null;
      });
    }
  }, [pollData, pollFailure, breaker, publishIntent, refresh]);
  useEffect(() => {
    const pending = restartRejoinPendingRef.current;
    if (!joinedCampaignId || !pending) return;
    restartRejoinPendingRef.current = null;
    // A seat taken long after the restart is the agent's own doing.
    if (Date.now() - pending.sinceMs > SERVER_RESTART_GIVE_UP_MS) return;
    toast.success('Back in the pool — the update is done.');
  }, [joinedCampaignId]);

  useEffect(() => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(POOL_INTENT_CHANNEL);
    } catch {
      return;
    }
    intentChannelRef.current = channel;
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const intent = event.data as Partial<PoolIntent> | null;
      if (!intent || typeof intent.id !== 'string') return;
      if (intent.campaignId !== null && typeof intent.campaignId !== 'string') return;
      if (intentRef.current?.id === intent.id) return;
      if (intent.campaignId !== joinedCampaignIdRef.current) breaker.reset();
      setIntent({ id: intent.id, campaignId: intent.campaignId });
    };
    return () => {
      if (intentChannelRef.current === channel) intentChannelRef.current = null;
      channel.close();
    };
  }, [breaker, setIntent]);

  const previousJoinedCampaignIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (previousJoinedCampaignIdRef.current === joinedCampaignId) return;
    previousJoinedCampaignIdRef.current = joinedCampaignId;
    if (joinedCampaignId) {
      if (intentRef.current === undefined) {
        publishIntent(joinedCampaignId);
      } else if (intentRef.current.campaignId !== joinedCampaignId) {
        breaker.reset();
        return;
      }
      breaker.joined(Date.now());
      setLineWarning(false);
      return;
    }
    if (breaker.vanished(Date.now(), busyWithNonPoolCallRef.current)) {
      setLineWarning(true);
      publishIntent(null);
    }
  }, [joinedCampaignId, breaker, publishIntent]);
  useEffect(() => {
    if (joinedCampaignId && member?.state === 'connecting') {
      if (member.linePending) breaker.waitingForLine();
      else breaker.connecting(Date.now());
    } else if (joinedCampaignId && member?.state) {
      breaker.lineUp();
    }
  }, [joinedCampaignId, member?.state, member?.linePending, breaker]);

  // The seat is waiting for this phone's registration. The switch can expire
  // a binding without the client hearing — the phone still reads Registered —
  // so ask for a fresh REGISTER now rather than waiting out the refresh
  // timer. Re-asked on every snapshot while the wait lasts; the hook throttles.
  const reRegister = softphone?.reRegister;
  useEffect(() => {
    if (member?.linePending && reRegister) reRegister('pool_line_pending');
  }, [member, reRegister]);

  const membershipPresent = membership !== null;
  useEffect(() => {
    if (
      !eligible
      || membershipPresent
      || !desiredCampaignId
      || !live
      || busyWithNonPoolCall
      || orphanedPoolLine
      || phoneReconnecting
      || pendingAction
    ) {
      return;
    }
    const target = desiredCampaignId;
    let cancelled = false;
    let linePrecondition = false;
    let timer: ReturnType<typeof setTimeout>;
    const attempt = (): void => {
      if (cancelled || intentRef.current?.campaignId !== target) return;
      // Asking again before the phone says it is registered can only collect
      // another refusal.
      if (linePrecondition && !phoneRegisteredRef.current) {
        timer = setTimeout(attempt, REJOIN_LINE_RETRY_MS);
        return;
      }
      void reconcileJoin(target).then((outcome) => {
        refresh();
        if (outcome.status === 'cleanup_failed') {
          toast.error(
            `The pool reconnected a moment before you left and could not be exited — press Leave pool again. (${outcome.error.message})`
          );
        } else if (outcome.status === 'failed' && !cancelled) {
          linePrecondition = isLinePreconditionFailure(outcome.error);
          timer = setTimeout(attempt, linePrecondition ? REJOIN_LINE_RETRY_MS : REJOIN_RETRY_MS);
        }
      });
    };
    timer = setTimeout(attempt, REJOIN_INITIAL_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [eligible, membershipPresent, desiredCampaignId, live, busyWithNonPoolCall, orphanedPoolLine, phoneReconnecting, pendingAction, reconcileJoin, refresh]);

  useEffect(() => {
    let carried: string | null;
    try {
      carried = sessionStorage.getItem(REJOIN_AFTER_RELOAD_KEY);
      sessionStorage.removeItem(REJOIN_AFTER_RELOAD_KEY);
    } catch {
      // Storage unavailable: the agent rejoins by hand, as after any reload.
      return;
    }
    if (carried) setIntent({ id: crypto.randomUUID(), campaignId: carried });
  }, [setIntent]);

  // Mid-call rescue (lib/pool-call-rescue): the owner tab remembers its live
  // call across a reload, and the reloaded tab takes it back once its phone is
  // online — the only trigger when the dying tab sent no BYE.
  const hasRuntimeLease = softphone?.hasRuntimeLease ?? false;
  const poolLineLive = softphone?.poolLineLive ?? false;
  const liveCallMarkerRef = useRef<PoolLiveCallMarker | null>(null);
  const carriedLiveCallRef = useRef<{ marker: PoolLiveCallMarker; bootAt: number } | null>(null);
  useEffect(() => {
    try {
      const carried = parseLiveCallMarker(sessionStorage.getItem(LIVE_CALL_STORAGE_KEY));
      liveCallMarkerRef.current = carried;
      if (carried) carriedLiveCallRef.current = { marker: carried, bootAt: performance.now() };
    } catch {
      // Storage unavailable: no claim; a rescue started by the old tab's BYE still lands.
    }
  }, []);
  useEffect(() => {
    const step = liveCallMarkerStep({ membership, hasRuntimeLease, marker: liveCallMarkerRef.current });
    if (step.kind === 'keep') return;
    try {
      if (step.kind === 'write') sessionStorage.setItem(LIVE_CALL_STORAGE_KEY, JSON.stringify(step.marker));
      else sessionStorage.removeItem(LIVE_CALL_STORAGE_KEY);
      liveCallMarkerRef.current = step.kind === 'write' ? step.marker : null;
    } catch {
      // Storage unavailable: this call just cannot be claimed after a reload.
    }
  }, [membership, hasRuntimeLease]);

  const claimInFlightRef = useRef(false);
  const nextClaimAtRef = useRef(0);
  const claimRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [claimRetryTick, setClaimRetryTick] = useState(0);
  useEffect(() => () => {
    if (claimRetryTimerRef.current !== null) clearTimeout(claimRetryTimerRef.current);
  }, []);
  useEffect(() => {
    const carried = carriedLiveCallRef.current;
    if (!carried || claimInFlightRef.current) return;
    const legId = carried.marker.contactLegId;
    if (carriedCallEnded(carried.marker, membership)) {
      carriedLiveCallRef.current = null;
      recordClientTelemetryEvent('reload_call_lost', 'warning', { legId, reason: 'ended_before_claim' });
      return;
    }
    if (poolLineLive) {
      // The rescue leg reached this tab: the call is back, nothing to claim.
      carriedLiveCallRef.current = null;
      return;
    }
    if (!claimReady({ carried: carried.marker, membership, hasRuntimeLease, phoneOnline: phoneRegistered, poolLineLive })) return;
    if (performance.now() - carried.bootAt > POOL_CALL_RESCUE_WINDOW_MS) {
      carriedLiveCallRef.current = null;
      recordClientTelemetryEvent('reload_call_lost', 'warning', { legId, reason: 'claim_window' });
      return;
    }
    if (performance.now() < nextClaimAtRef.current) return;
    claimInFlightRef.current = true;
    pooledDialerApi.claimRescue(carried.marker.campaignId, legId).then(
      () => {
        carriedLiveCallRef.current = null;
        recordClientTelemetryEvent('reload_call_rescue_claimed', 'info', { legId });
        refresh();
      },
      (error: unknown) => {
        if (error instanceof ApiError && error.status === 409) {
          nextClaimAtRef.current = performance.now() + RESCUE_CLAIM_RETRY_MS;
          if (claimRetryTimerRef.current !== null) clearTimeout(claimRetryTimerRef.current);
          claimRetryTimerRef.current = setTimeout(() => setClaimRetryTick((tick) => tick + 1), RESCUE_CLAIM_RETRY_MS);
          return;
        }
        carriedLiveCallRef.current = null;
        recordClientTelemetryEvent('reload_call_lost', 'warning', {
          legId,
          reason: 'claim_refused',
          status: error instanceof ApiError ? error.status : null,
        });
      }
    ).finally(() => {
      claimInFlightRef.current = false;
    });
  }, [membership, hasRuntimeLease, phoneRegistered, poolLineLive, claimRetryTick, refresh]);

  const rescueWatchRef = useRef<RescueWatch | null>(null);
  useEffect(() => {
    // One report per rescue: the owner tab is the one the call comes back to.
    if (!hasRuntimeLease) return;
    const step = rescueWatchStep(rescueWatchRef.current, member, performance.now());
    rescueWatchRef.current = step.watch;
    if (step.event) {
      recordClientTelemetryEvent(
        step.event.name,
        step.event.name === 'reload_call_lost' ? 'warning' : 'info',
        step.event.payload
      );
    }
  }, [member, hasRuntimeLease]);

  const releaseLineForReload = useCallback(async () => {
    if (!membership) return true;
    // Marker first: if storage refuses, nothing has left the pool yet.
    sessionStorage.setItem(REJOIN_AFTER_RELOAD_KEY, membership.campaignId);
    try {
      await pooledDialerApi.leave(membership.campaignId, { onlyIfParked: true });
    } catch (error) {
      sessionStorage.removeItem(REJOIN_AFTER_RELOAD_KEY);
      if (error instanceof ApiError && error.status === 409) return false;
      throw error;
    }
    // Local only: the reload is imminent and the new page republishes the
    // intent. Clearing it here keeps the rejoin effect from racing the reload.
    setIntent({ id: crypto.randomUUID(), campaignId: null });
    return true;
  }, [membership, setIntent]);

  const joining = pendingAction === 'join';
  const leaving = pendingAction === 'leave';

  const value = useMemo<PoolMembershipValue>(
    () => ({
      membership,
      upNext,
      refresh,
      join,
      leave,
      joining,
      leaving,
      desiredCampaignId,
      lineWarning,
      busyWithNonPoolCall,
      orphanedPoolLine,
      phoneReconnecting,
      serverRestarting: serverRestart !== null,
      parked,
      releaseLineForReload,
    }),
    [membership, upNext, refresh, join, leave, joining, leaving, desiredCampaignId, lineWarning, busyWithNonPoolCall, orphanedPoolLine, phoneReconnecting, serverRestart, parked, releaseLineForReload]
  );

  return (
    <PoolMembershipContext.Provider value={value}>
      {children}
      <LiveCallStrip membership={value.membership} upNext={value.upNext} />
    </PoolMembershipContext.Provider>
  );
}
